import React, { useMemo } from 'react';
import DataTableHeader from './DataTableHeader';
import { useCustomSettingsContext } from '../../../CustomSettings/Services/CustomSettingsContext';
import DataTableItem from './DataTableItem';
import DataTableCount from './DataTableCount';

interface Identifiable {
	id: string;
}

export interface DataTableColumn<TItem, TTable> {
	key: string;
	label?: string;
	renderItem: (
		item: TItem,
		table: TTable,
	) => React.ReactElement | string | number | undefined;
	bold?: boolean;
	visible?: boolean;
}

interface Props<TItem extends Identifiable, TTable> {
	data: TItem[];
	table: TTable;
	columns: DataTableColumn<TItem, TTable>[];
	rowStyle?: React.CSSProperties;
	totalCount?: number;
}

export const DataTable = <TItem extends Identifiable, TTable>({
	data,
	table,
	columns,
	rowStyle,
	totalCount,
}: Props<TItem, TTable>): React.ReactElement => {
	const { customSettings } = useCustomSettingsContext();
	const cols: DataTableColumn<TItem, TTable>[] = useMemo(() => {
		const visibleColumns = columns.filter(item => item.visible !== false);
		if (!customSettings) {
			return visibleColumns;
		}
		const settings = customSettings as Record<string, boolean | undefined>;
		return visibleColumns.filter(item => settings[item.key] || item.visible);
	}, [customSettings, columns]);

	return (
		<>
			{typeof totalCount === 'number' && <DataTableCount count={totalCount} />}
			<table style={{ width: '100%' }}>
				<DataTableHeader<TItem, TTable> columns={cols} />
				<tbody>
					{data.map(item => (
						<DataTableItem
							key={item.id}
							item={item}
							table={table}
							columns={cols}
							style={rowStyle}
						/>
					))}
				</tbody>
			</table>
		</>
	);
};

export default DataTable;
